import { useEffect, useState } from "react";
import Confetti from "../Confetti";
import vw from "../../api/virtualWorld.api";

const cr = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

/**
 * Einmalige Zeremonie nach Team-Turnier-Podium (wie Liga-Meisterschaft mit Konfetti).
 */
const CupCeremonyOverlay = ({ userId, ceremony, onDone, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [local, setLocal] = useState(ceremony || null);

  useEffect(() => {
    setLocal(ceremony || null);
    setErr("");
  }, [ceremony]);

  if (!local) return null;

  const champion = Boolean(local.champion) || Number(local.place) === 1;
  const title =
    local.title ||
    (champion ? "Turniersieger!" : `Platz ${local.place}`);

  const dismiss = async () => {
    setLocal(null);
    if (onRefresh) await onRefresh();
    if (onDone) onDone();
  };

  const ack = async () => {
    if (!userId || busy) return;
    setBusy(true);
    setErr("");
    try {
      await vw.acknowledgeCupCeremony(userId);
      await dismiss();
    } catch (e) {
      const status = e?.status || e?.data?.status;
      const msg = String(e?.data?.message || e?.message || "");
      if (status === 404 || /Keine|kein/i.test(msg)) {
        await dismiss();
        return;
      }
      setErr(e?.data?.message || e?.message || "Zeremonie konnte nicht bestätigt werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/75"
      role="dialog"
      aria-modal="true"
    >
      <Confetti />
      <div className="relative max-w-lg w-full rounded-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800 border border-amber-300/50 p-7 shadow-2xl overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-30 text-3xl leading-none select-none">
          {champion ? "🏆✨🎊✨🏆✨🎊✨🏆" : "🥈✨🏅✨🥉✨🏅"}
        </div>
        <div className="relative space-y-4">
          <p className="text-xs uppercase tracking-widest opacity-60">
            Team-Turnier
            {local.seasonNumber ? ` · Saison ${local.seasonNumber}` : ""}
          </p>
          <h2 className="text-3xl font-black">{title}</h2>
          <p className="opacity-80">
            {local.body ||
              (champion
                ? `${local.clubName || "Dein Club"} gewinnt das Team-Turnier.`
                : `${local.clubName || "Dein Club"} landet auf Platz ${local.place}.`)}
          </p>
          <ul className="space-y-2 text-sm">
            {local.place ? (
              <li className="flex justify-between gap-4">
                <span>Platzierung</span>
                <strong>{local.place}.</strong>
              </li>
            ) : null}
            {Number(local.prize) > 0 ? (
              <li className="flex justify-between gap-4">
                <span>Preisgeld eingespielt</span>
                <strong className="text-emerald-700 dark:text-emerald-300">{cr(local.prize)}</strong>
              </li>
            ) : null}
            {Number(local.pot) > 0 ? (
              <li className="flex justify-between gap-4 opacity-70">
                <span>Turniertopf</span>
                <span>{cr(local.pot)}</span>
              </li>
            ) : null}
          </ul>
          <button
            type="button"
            disabled={busy}
            onClick={ack}
            className="w-full px-5 py-3 rounded-xl bg-amber-700 text-amber-50 font-bold disabled:opacity-50"
          >
            {busy ? "…" : champion ? "Pokal einpacken" : "Weiter"}
          </button>
          {err ? <p className="text-sm text-red-600">{err}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default CupCeremonyOverlay;
